/**
 * The demo shows (`acp seed-rundown`, and the SPA onboarding wizard's first rundown): a realistic demo rundown — an evening news show with an
 * opening, two news segments and a weather segment; parts with durations,
 * scripts and transitions; pieces on camera, VT, graphics and audio layers —
 * built as ordinary creation patches, so seeding exercises exactly the write
 * path every editor uses.
 *
 * The patches come in three batches (segments, then parts, then pieces): a
 * child's parent must exist when the child is validated (PLAN §6.4), and
 * sending parents in an earlier request makes that true whatever order a host
 * validates a batch in.
 */
import { ordsBetween } from '@acp/domain/ordering.js';
import type { JsonValue } from '@acp/domain/primitives.js';
import type { RowPatch } from '@acp/domain/row.js';
import { textReplaceUpdate } from '@acp/domain/text.js';

type PieceSpec = { readonly name: string; readonly pieceType: string; readonly sourceLayer: string; readonly outputLayer: string; readonly extra: Readonly<Record<string, JsonValue>> };
type PartSpec = {
  readonly title: string;
  readonly expectedDuration: number;
  /** A text field (Yjs, PLAN §6.1): sent as `patch.text`, never `set`. */
  readonly script: string | null;
  readonly extra: Readonly<Record<string, JsonValue>>;
  readonly pieces: readonly PieceSpec[];
};
type SegmentSpec = { readonly name: string; readonly parts: readonly PartSpec[] };

const cam = (n: number): PieceSpec => ({ name: `Camera ${String(n)}`, pieceType: 'camera', sourceLayer: 'studio0_camera', outputLayer: 'pgm', extra: { 'params.input': n } });
const vt = (clip: string, duration: number): PieceSpec => ({
  name: `VT ${clip}`,
  pieceType: 'vt',
  sourceLayer: 'studio0_vt',
  outputLayer: 'pgm',
  extra: { 'params.clip': clip, enable: { start: 0, duration } },
});
const lowerThird = (line1: string, line2: string): PieceSpec => ({
  name: `Lower third: ${line1}`,
  pieceType: 'graphics.lower-third',
  sourceLayer: 'studio0_graphics_lower',
  outputLayer: 'overlay',
  extra: { 'params.line1': line1, 'params.line2': line2, enable: { start: 2_000, duration: 6_000 } },
});
const music = (track: string): PieceSpec => ({
  name: `Music: ${track}`,
  pieceType: 'audio',
  sourceLayer: 'studio0_audio_bed',
  outputLayer: 'audio',
  extra: { 'params.track': track, lifespan: 'segment-end' },
});

/** The show. Durations are milliseconds (the domain's `durationMsSchema`). */
export const DEMO_SHOW: readonly SegmentSpec[] = [
  {
    name: 'Opening',
    parts: [
      { title: 'Headlines', expectedDuration: 30_000, script: 'Good evening. Tonight’s headlines…', extra: {}, pieces: [cam(1), music('Theme'), vt('headlines-open', 25_000)] },
      { title: 'Welcome', expectedDuration: 15_000, script: null, extra: { autoNext: false }, pieces: [cam(2), lowerThird('Anna Berg', 'Presenter')] },
    ],
  },
  {
    name: 'Top story',
    parts: [
      { title: 'Intro', expectedDuration: 20_000, script: 'Our top story tonight…', extra: {}, pieces: [cam(1)] },
      {
        title: 'Report',
        expectedDuration: 95_000,
        script: null,
        extra: { inTransition: { blockTakeDuration: 1_000, previousPartKeepalive: 500, contentDelay: 0 } },
        pieces: [vt('top-story-report', 95_000), lowerThird('Ola Nordmann', 'Reporter')],
      },
      { title: 'Studio guest', expectedDuration: 120_000, script: null, extra: {}, pieces: [cam(3), lowerThird('Dr. Kari Lie', 'Economist')] },
    ],
  },
  {
    name: 'News in brief',
    parts: [
      { title: 'Brief 1', expectedDuration: 25_000, script: null, extra: { autoNext: true }, pieces: [vt('brief-1', 25_000)] },
      { title: 'Brief 2', expectedDuration: 25_000, script: null, extra: { autoNext: true }, pieces: [vt('brief-2', 25_000)] },
    ],
  },
  {
    name: 'Weather',
    parts: [
      { title: 'Weather map', expectedDuration: 60_000, script: null, extra: {}, pieces: [cam(4), { name: 'Weather map', pieceType: 'graphics.fullscreen', sourceLayer: 'studio0_graphics_full', outputLayer: 'pgm', extra: { 'params.template': 'weather' } }] },
      { title: 'Goodbye', expectedDuration: 10_000, script: null, extra: { outTransition: { duration: 1_000 } }, pieces: [cam(1), music('Outro')] },
    ],
  },
];

const studioPiece = (name: string, pieceType: string, sourceLayer: string, outputLayer: string, extra: Readonly<Record<string, JsonValue>>): PieceSpec => ({
  name,
  pieceType,
  sourceLayer,
  outputLayer,
  extra,
});

/**
 * A show for `acp studio-template`'s studio (the three simulated devices):
 * its piece types (`camera`, `vt`, `lower-third`, `fullscreen`) and layers,
 * and clips the simulated video server has (`opener` 10 s, `package-a` 90 s).
 * The opener auto-nexts into the studio part — the playout checks rely on
 * that (`just e2e-playout`, the `test:playout` gate).
 */
export const STUDIO_SHOW: readonly SegmentSpec[] = [
  {
    name: 'Open',
    parts: [
      {
        title: 'Welcome',
        expectedDuration: 8_000,
        script: 'Good evening and welcome.',
        extra: {},
        pieces: [
          studioPiece('Camera 1', 'camera', 'cam', 'pgm', { 'params.input': 1 }),
          studioPiece('Presenter', 'lower-third', 'lt', 'overlay', { 'params.name': 'Anna Berg', 'params.title': 'Presenter', enable: { start: 500, duration: 6_000 } }),
        ],
      },
      { title: 'Opener', expectedDuration: 4_000, script: null, extra: { autoNext: true }, pieces: [studioPiece('VT opener', 'vt', 'vt', 'pgm', { 'params.clip': 'opener' })] },
      { title: 'Studio', expectedDuration: 20_000, script: null, extra: {}, pieces: [studioPiece('Camera 2', 'camera', 'cam', 'pgm', { 'params.input': 2 })] },
    ],
  },
  {
    name: 'Report',
    parts: [
      {
        title: 'Package',
        expectedDuration: 90_000,
        script: null,
        extra: {},
        pieces: [
          studioPiece('VT package-a', 'vt', 'vt', 'pgm', { 'params.clip': 'package-a' }),
          studioPiece('Reporter', 'lower-third', 'lt', 'overlay', { 'params.name': 'Ola Nordmann', 'params.title': 'Reporter', enable: { start: 2_000, duration: 5_000 } }),
        ],
      },
      {
        title: 'Weather',
        expectedDuration: 30_000,
        script: null,
        extra: {},
        pieces: [studioPiece('Weather map', 'fullscreen', 'full', 'pgm', { 'params.template': 'weather' })],
      },
    ],
  },
  { name: 'Close', parts: [{ title: 'Goodbye', expectedDuration: 10_000, script: null, extra: {}, pieces: [studioPiece('Camera 1', 'camera', 'cam', 'pgm', { 'params.input': 1 })] }] },
];

/**
 * A show for the vMix studio (`vmixTemplate.ts`, docs/vmix.md): the vMix show
 * style's piece types and layers against the inputs docs/vmix.md sets up —
 * cameras 1–3, VT A and VT B, the lower-third and full-screen titles. The
 * opener's part lasts as long as it plays (6 s) and auto-nexts: vMix does not
 * say when a clip ends, the part's duration does (set it to your clip's length).
 * The `vmix` playout spec (`apps/production/tests/vmix.spec.ts`) plays it.
 */
export const VMIX_SHOW: readonly SegmentSpec[] = [
  {
    name: 'Open',
    parts: [
      {
        title: 'Welcome',
        expectedDuration: 8_000,
        script: 'Good evening and welcome.',
        extra: {},
        pieces: [
          studioPiece('Camera 1', 'camera', 'cam', 'pgm', { 'params.input': 1 }),
          studioPiece('Presenter', 'lower-third', 'lt', 'overlay', { 'params.name': 'Anna Berg', 'params.title': 'Presenter', enable: { start: 500, duration: 6_000 } }),
        ],
      },
      { title: 'Opener', expectedDuration: 6_000, script: null, extra: { autoNext: true }, pieces: [studioPiece('VT A: opener', 'clip', 'vt', 'pgm', {})] },
      {
        title: 'Studio',
        expectedDuration: 20_000,
        script: 'Tonight: a report from the city, and the weather.',
        extra: {},
        pieces: [studioPiece('Camera 2 (mix)', 'camera', 'cam', 'pgm', { 'params.input': 2, 'params.transition': 'Fade', 'params.duration': 500 })],
      },
    ],
  },
  {
    name: 'Report',
    parts: [
      {
        title: 'Package',
        expectedDuration: 20_000,
        script: null,
        extra: {},
        pieces: [
          studioPiece('VT B: report', 'clip-b', 'vt', 'pgm', {}),
          studioPiece('Reporter', 'lower-third', 'lt', 'overlay', { 'params.name': 'Ola Nordmann', 'params.title': 'Reporter', enable: { start: 2_000, duration: 5_000 } }),
        ],
      },
      {
        title: 'Weather',
        expectedDuration: 15_000,
        script: null,
        extra: {},
        pieces: [studioPiece('Weather', 'fullscreen', 'full', 'pgm', { 'params.headline': 'Weather: sunny spells' })],
      },
    ],
  },
  {
    name: 'Close',
    parts: [
      {
        title: 'Goodbye',
        expectedDuration: 10_000,
        script: 'Goodnight.',
        extra: {},
        pieces: [studioPiece('Camera 3 (merge)', 'camera', 'cam', 'pgm', { 'params.input': 3, 'params.transition': 'Merge', 'params.duration': 1_000 })],
      },
    ],
  },
];

/** The shows `acp seed-rundown --show` knows. */
export const SHOWS: Readonly<Record<string, readonly SegmentSpec[]>> = { news: DEMO_SHOW, studio: STUDIO_SHOW, vmix: VMIX_SHOW };

export type SeedWriter = {
  readonly hlc: () => string;
  readonly newId: () => string;
  readonly newOpId: () => string;
  readonly origin: string;
  readonly ordTag: string;
  readonly textClientId: number;
};

type Creation = {
  readonly kind: 'segment' | 'part' | 'piece';
  readonly parentId: string;
  readonly ord: string;
  readonly set: Readonly<Record<string, JsonValue>>;
  readonly text: Readonly<Record<string, string>>;
};

function creation(w: SeedWriter, rundownId: string, c: Creation): RowPatch {
  const { kind, parentId, ord, set } = c;
  const text = Object.keys(c.text).length > 0 ? { text: { ...c.text } } : {};
  return {
    rowId: w.newId(),
    set: { parentId, ord, ...set },
    ...text,
    create: { kind, rundownId, source: { system: 'native' }, lockedFields: [] },
    hlc: w.hlc(),
    origin: w.origin,
    clientOpId: w.newOpId(),
  };
}

export type SeedBatches = { readonly segments: RowPatch[]; readonly parts: RowPatch[]; readonly pieces: RowPatch[] };

export function seedPatches(show: readonly SegmentSpec[], rundownId: string, w: SeedWriter): SeedBatches {
  const out: SeedBatches = { segments: [], parts: [], pieces: [] };
  const segmentOrds = ordsBetween(null, null, show.length, w.ordTag);
  show.forEach((segment, si) => {
    const seg = creation(w, rundownId, { kind: 'segment', parentId: rundownId, ord: segmentOrds[si] ?? '', set: { name: segment.name }, text: {} });
    out.segments.push(seg);
    const partOrds = ordsBetween(null, null, segment.parts.length, w.ordTag);
    segment.parts.forEach((part, pi) => {
      const p = creation(w, rundownId, {
        kind: 'part',
        parentId: seg.rowId,
        ord: partOrds[pi] ?? '',
        set: { title: part.title, expectedDuration: part.expectedDuration, ...part.extra },
        text: part.script === null ? {} : { script: textReplaceUpdate(null, part.script, w.textClientId) },
      });
      out.parts.push(p);
      const pieceOrds = ordsBetween(null, null, part.pieces.length, w.ordTag);
      part.pieces.forEach((piece, xi) => {
        out.pieces.push(
          creation(w, rundownId, {
            kind: 'piece',
            parentId: p.rowId,
            ord: pieceOrds[xi] ?? '',
            set: { name: piece.name, pieceType: piece.pieceType, sourceLayer: piece.sourceLayer, outputLayer: piece.outputLayer, ...piece.extra },
            text: {},
          }),
        );
      });
    });
  });
  return out;
}
